import { useId } from "react";
import { LongText } from "@core/components/LongText";
import { useSortable } from "@core/components/Sortable";
import { Value } from "@core/components/Value";
import { AmmoCounter } from "./inventory";
import { useSm } from "./view";

/**
 * The card „Waffen“ of the equipment tab. The user orders whole weapons with all their profiles by the handle; the play page, the PDF
 * sheet and the Genesis export follow the order from the view.
 */
export function WeaponCard() {
  const { view, apply, language, value } = useSm();
  const hint = useId();
  const sortable = useSortable({
    items: view.weapons,
    keyOf: (w) => w.instance,
    labelOf: (w) => w.name,
    onCommit: (order) => apply({ type: "sm.orderWeapons", payload: { order } }),
  });
  const sortableRows = view.weapons.length > 1;
  return (
    <section className="card">
      <h3>Waffen</h3>
      {sortableRows && (
        <>
          <p id={hint} className="visually-hidden">
            Am Griff ziehen oder mit Leertaste aufnehmen, mit den Pfeiltasten verschieben und mit Leertaste ablegen.
          </p>
          <p className="visually-hidden" aria-live="polite">
            {sortable.announcement}
          </p>
        </>
      )}
      <div className="table-scroll">
        <table className="compact-table weapon-table" aria-label="Waffen">
          <thead>
            <tr>
              {sortableRows && (
                <th>
                  <span className="visually-hidden">Reihenfolge</span>
                </th>
              )}
              <th>Waffe</th>
              <th>FW</th>
              <th>Schaden</th>
              <th>WGS</th>
              <th>Merkmale</th>
            </tr>
          </thead>
          {sortable.items.map((w) => (
            <tbody key={w.instance} ref={sortable.itemRef(w.instance)} className={sortable.moving === w.instance ? "weapon-moving" : undefined} data-weapon={w.instance}>
              {w.profiles.map((p, i) => (
                <tr key={i}>
                  {sortableRows && i === 0 && (
                    <td rowSpan={w.profiles.length} className="sort-cell">
                      <button {...sortable.handleProps(w.instance)} className="sort-handle" aria-describedby={hint}>
                        <span aria-hidden="true">⠿</span>
                      </button>
                    </td>
                  )}
                  <td>
                    {w.name}
                    {w.profiles.length > 1 && <span className="muted"> ({p.kind})</span>}
                    {w.container && <span className="muted"> (im {w.container})</span>}
                    <div className="muted small">
                      {p.skill} · {p.attributes}
                      {/* Plain text here: the rows are dragged by touch; the play page shows the range with its derivation. */}
                      {p.rangeKey && ` · ${value(p.rangeKey)?.number ?? p.range} m`}
                    </div>
                    {i === 0 && <AmmoCounter weapon={w} />}
                  </td>
                  <td>
                    <Value valueKey={p.fw} />
                  </td>
                  <td>
                    <Value valueKey={p.damage} />
                  </td>
                  <td>
                    <Value valueKey={p.wgs} />
                  </td>
                  <td className="small">
                    {p.features.map((f) => f.name).join(", ")}
                    {p.features.map((f, j) => (
                      <LongText key={j} value={f.description} language={language} label={f.name} />
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          ))}
        </table>
      </div>
    </section>
  );
}
